import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { AccountHealthCheck } from "@/components/settings/AccountHealthCheck";

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useProfile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasEnsuredProfileRef = useRef(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isEnsuringProfile, setIsEnsuringProfile] = useState(false);

  // Initialize form when profile loads - using correct useEffect
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
    }
  }, [profile]);

  // If the profile row is missing (or failed to load), try to create it once.
  useEffect(() => {
    async function ensureProfileExists() {
      if (!user) return;

      setIsEnsuringProfile(true);
      try {
        const meta = (user.user_metadata ?? {}) as Record<string, any>;

        const first = (meta.first_name ?? meta.given_name ?? null) as string | null;
        const last = (meta.last_name ?? meta.family_name ?? null) as string | null;
        const computedName = [first, last].filter(Boolean).join(" ").trim();

        const computedNameOrNull = computedName ? computedName : null;
        const name = (meta.full_name ?? meta.name ?? computedNameOrNull) as string | null;
        const avatarUrl = (meta.avatar_url ?? meta.picture ?? null) as string | null;
        const email = user.email ?? "";

        if (!email) {
          throw new Error("Profil konnte nicht erstellt werden (fehlende E-Mail)." );
        }

        const { error } = await supabase
          .from("profiles")
          .upsert(
            {
              id: user.id,
              email,
              first_name: first,
              last_name: last,
              name,
              avatar_url: avatarUrl,
            },
            { onConflict: "id" },
          );

        if (error) throw error;

        await queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      } catch (error: any) {
        // allow manual retry
        hasEnsuredProfileRef.current = false;
      toast({
        title: "Profil konnte nicht geladen werden",
        description: error?.message || "Bitte versuche es erneut.",
        variant: "destructive",
      });
      } finally {
        setIsEnsuringProfile(false);
      }
    }

    if (!user) return;
    if (profileLoading) return;
    if (profile) return;
    if (hasEnsuredProfileRef.current) return;

    hasEnsuredProfileRef.current = true;
    void ensureProfileExists();
  }, [user, profile, profileLoading, queryClient, toast]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wählen Sie ein Bild aus.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Datei zu groß",
        description: "Bitte wählen Sie ein Bild kleiner als 5MB.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingAvatar(true);

    try {
      // Delete old avatar if exists
      if (profile?.avatar_url) {
        const oldPath = profile.avatar_url.split("/").slice(-2).join("/");
        await supabase.storage.from("avatars").remove([oldPath]);
      }

      // Upload new avatar
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/avatar.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      // Update profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      setAvatarPreview(publicUrl);
      
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ["profile"] });

      toast({
        title: "Avatar aktualisiert",
        description: "Dein Profilbild wurde erfolgreich aktualisiert.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Avatar konnte nicht hochgeladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user || !profile?.avatar_url) return;

    setIsUploadingAvatar(true);

    try {
      // Delete from storage
      const oldPath = profile.avatar_url.split("/").slice(-2).join("/");
      await supabase.storage.from("avatars").remove([oldPath]);

      // Update profile
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", user.id);

      if (error) throw error;

      setAvatarPreview(null);
      
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ["profile"] });

      toast({
        title: "Avatar entfernt",
        description: "Dein Profilbild wurde entfernt.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Avatar konnte nicht entfernt werden.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ 
          first_name: firstName || null,
          last_name: lastName || null,
          name: `${firstName} ${lastName}`.trim() || null
        })
        .eq("id", user.id);

      if (error) throw error;

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ["profile"] });

      toast({
        title: "Gespeichert",
        description: "Dein Profil wurde aktualisiert.",
      });
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Profil konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || profileLoading || isEnsuringProfile) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!profile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Profil nicht verfügbar</CardTitle>
          <CardDescription>
            Wir konnten dein Profil gerade nicht laden. Bitte versuche es erneut.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {profileError && (
            <p className="text-sm text-muted-foreground">
              Details: {profileError instanceof Error ? profileError.message : "Unbekannter Fehler"}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              hasEnsuredProfileRef.current = false;
              await queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
            }}
          >
            Neu laden
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentAvatarUrl = avatarPreview || profile.avatar_url;
  const userInitials = profile.first_name && profile.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : profile.email?.slice(0, 2).toUpperCase() || "U";

  return (
    <div className="max-w-4xl space-y-6 pb-20 md:pb-6">
      {/* Account Health Check */}
      <AccountHealthCheck userId={user.id} profileExists={!!profile} />
      <Card>
        <CardHeader>
          <CardTitle>Profilbild</CardTitle>
          <CardDescription>
            Lade ein Profilbild hoch. Empfohlen: Quadratisches Bild, max. 5MB.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <Avatar className="h-20 w-20 sm:h-24 sm:w-24 shrink-0">
              {currentAvatarUrl && (
                <AvatarImage src={currentAvatarUrl} alt="Profilbild" />
              )}
              <AvatarFallback className="bg-primary text-primary-foreground text-xl sm:text-2xl">
                {isUploadingAvatar ? <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" /> : userInitials}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex flex-col items-center sm:items-start gap-2 w-full sm:w-auto">
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAvatarClick}
                  disabled={isUploadingAvatar}
                  className="flex-1 sm:flex-none"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {currentAvatarUrl ? "Ändern" : "Hochladen"}
                </Button>
                
                {currentAvatarUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveAvatar}
                    disabled={isUploadingAvatar}
                    className="flex-1 sm:flex-none"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Entfernen
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground text-center sm:text-left">
                JPG, PNG, WEBP oder GIF. Max. 5MB.
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Persönliche Informationen</CardTitle>
          <CardDescription>
            Verwalte deine persönlichen Informationen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Vorname</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Max"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Nachname</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Mustermann"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSaving}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                disabled
                className="bg-muted"
              />
              <p className="text-sm text-muted-foreground">
                E-Mail ändern unter{" "}
                <Link 
                  to="/settings/security" 
                  className="text-primary hover:underline"
                >
                  Sicherheitseinstellungen
                </Link>
              </p>
            </div>

            <Button
              type="submit"
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Änderungen speichern
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
